import { type ChartFamily, getChartTypeDef, normalizeChartTypeId } from "./chartTypeRegistry";

export type ChartOptionFieldType = "text" | "number" | "boolean" | "select" | "color";

export type ChartOptionPanelId =
  | "general"
  | "titleLegend"
  | "tooltip"
  | "grid"
  | "axes"
  | "series"
  | "visualMap"
  | "calendar"
  | "radar"
  | "gauge"
  | "hierarchical"
  | "flow";

export interface ChartOptionFieldDef {
  id: string;
  path: string;
  type: ChartOptionFieldType;
  labelKey: string;
  hintKey?: string;
  panel: ChartOptionPanelId;
  families?: ChartFamily[];
  chartTypes?: string[];
  excludeChartTypes?: string[];
  options?: { value: string | number | boolean; labelKey: string }[];
  min?: number;
  max?: number;
  step?: number;
}

const ALL_FAMILIES: ChartFamily[] = [
  "cartesian",
  "circular",
  "radial",
  "hierarchical",
  "matrix",
  "flow",
  "statistical",
];

const CARTESIAN: ChartFamily[] = ["cartesian", "statistical"];
const CIRCULAR: ChartFamily[] = ["circular"];

export const CHART_OPTION_PANEL_ORDER: ChartOptionPanelId[] = [
  "general",
  "titleLegend",
  "tooltip",
  "grid",
  "axes",
  "series",
  "visualMap",
  "calendar",
  "radar",
  "gauge",
  "hierarchical",
  "flow",
];

export const CHART_OPTION_FIELDS: ChartOptionFieldDef[] = [
  {
    id: "backgroundColor",
    path: "backgroundColor",
    type: "color",
    labelKey: "chartOptions.fields.backgroundColor",
    panel: "general",
    families: ALL_FAMILIES,
  },
  {
    id: "animation",
    path: "animation",
    type: "boolean",
    labelKey: "chartOptions.fields.animation",
    panel: "general",
    families: ALL_FAMILIES,
  },
  {
    id: "animationDuration",
    path: "animationDuration",
    type: "number",
    labelKey: "chartOptions.fields.animationDuration",
    panel: "general",
    min: 0,
    max: 5000,
    step: 100,
    families: ALL_FAMILIES,
  },
  {
    id: "titleText",
    path: "title.text",
    type: "text",
    labelKey: "chartOptions.fields.titleText",
    hintKey: "chartOptions.hints.titleText",
    panel: "titleLegend",
    families: ALL_FAMILIES,
  },
  {
    id: "titleSubtext",
    path: "title.subtext",
    type: "text",
    labelKey: "chartOptions.fields.titleSubtext",
    panel: "titleLegend",
    families: ALL_FAMILIES,
  },
  {
    id: "titleLeft",
    path: "title.left",
    type: "select",
    labelKey: "chartOptions.fields.titleLeft",
    panel: "titleLegend",
    families: ALL_FAMILIES,
    options: [
      { value: "left", labelKey: "chartOptions.pos.left" },
      { value: "center", labelKey: "chartOptions.pos.center" },
      { value: "right", labelKey: "chartOptions.pos.right" },
    ],
  },
  {
    id: "titleTop",
    path: "title.top",
    type: "select",
    labelKey: "chartOptions.fields.titleTop",
    panel: "titleLegend",
    families: ALL_FAMILIES,
    options: [
      { value: "top", labelKey: "chartOptions.pos.top" },
      { value: "middle", labelKey: "chartOptions.pos.middle" },
      { value: "bottom", labelKey: "chartOptions.pos.bottom" },
    ],
  },
  {
    id: "legendShow",
    path: "legend.show",
    type: "boolean",
    labelKey: "chartOptions.fields.legendShow",
    panel: "titleLegend",
    families: ALL_FAMILIES,
    excludeChartTypes: ["gauge", "tree", "sankey", "themeRiver"],
  },
  {
    id: "legendOrient",
    path: "legend.orient",
    type: "select",
    labelKey: "chartOptions.fields.legendOrient",
    panel: "titleLegend",
    families: ALL_FAMILIES,
    excludeChartTypes: ["gauge", "tree", "sankey", "themeRiver"],
    options: [
      { value: "horizontal", labelKey: "chartOptions.orient.horizontal" },
      { value: "vertical", labelKey: "chartOptions.orient.vertical" },
    ],
  },
  {
    id: "legendLeft",
    path: "legend.left",
    type: "select",
    labelKey: "chartOptions.fields.legendLeft",
    panel: "titleLegend",
    families: ALL_FAMILIES,
    excludeChartTypes: ["gauge", "tree", "sankey", "themeRiver"],
    options: [
      { value: "left", labelKey: "chartOptions.pos.left" },
      { value: "center", labelKey: "chartOptions.pos.center" },
      { value: "right", labelKey: "chartOptions.pos.right" },
    ],
  },
  {
    id: "legendTop",
    path: "legend.top",
    type: "select",
    labelKey: "chartOptions.fields.legendTop",
    panel: "titleLegend",
    families: ALL_FAMILIES,
    excludeChartTypes: ["gauge", "tree", "sankey", "themeRiver"],
    options: [
      { value: "top", labelKey: "chartOptions.pos.top" },
      { value: "middle", labelKey: "chartOptions.pos.middle" },
      { value: "bottom", labelKey: "chartOptions.pos.bottom" },
    ],
  },
  {
    id: "legendType",
    path: "legend.type",
    type: "select",
    labelKey: "chartOptions.fields.legendType",
    panel: "titleLegend",
    families: ALL_FAMILIES,
    excludeChartTypes: ["gauge", "tree", "sankey", "themeRiver"],
    options: [
      { value: "plain", labelKey: "chartOptions.legendType.plain" },
      { value: "scroll", labelKey: "chartOptions.legendType.scroll" },
    ],
  },
  {
    id: "tooltipTrigger",
    path: "tooltip.trigger",
    type: "select",
    labelKey: "chartOptions.fields.tooltipTrigger",
    panel: "tooltip",
    families: ALL_FAMILIES,
    options: [
      { value: "item", labelKey: "chartOptions.tooltipTrigger.item" },
      { value: "axis", labelKey: "chartOptions.tooltipTrigger.axis" },
      { value: "none", labelKey: "chartOptions.tooltipTrigger.none" },
    ],
  },
  {
    id: "tooltipConfine",
    path: "tooltip.confine",
    type: "boolean",
    labelKey: "chartOptions.fields.tooltipConfine",
    panel: "tooltip",
    families: ALL_FAMILIES,
  },
  {
    id: "tooltipAxisPointer",
    path: "tooltip.axisPointer.type",
    type: "select",
    labelKey: "chartOptions.fields.tooltipAxisPointer",
    panel: "tooltip",
    families: CARTESIAN,
    options: [
      { value: "line", labelKey: "chartOptions.axisPointer.line" },
      { value: "shadow", labelKey: "chartOptions.axisPointer.shadow" },
      { value: "cross", labelKey: "chartOptions.axisPointer.cross" },
      { value: "none", labelKey: "chartOptions.axisPointer.none" },
    ],
  },
  {
    id: "gridLeft",
    path: "grid.left",
    type: "text",
    labelKey: "chartOptions.fields.gridLeft",
    hintKey: "chartOptions.hints.gridPercent",
    panel: "grid",
    families: [...CARTESIAN, "matrix"],
    excludeChartTypes: ["calendarHeatmap"],
  },
  {
    id: "gridRight",
    path: "grid.right",
    type: "text",
    labelKey: "chartOptions.fields.gridRight",
    hintKey: "chartOptions.hints.gridPercent",
    panel: "grid",
    families: [...CARTESIAN, "matrix"],
    excludeChartTypes: ["calendarHeatmap"],
  },
  {
    id: "gridTop",
    path: "grid.top",
    type: "text",
    labelKey: "chartOptions.fields.gridTop",
    hintKey: "chartOptions.hints.gridPercent",
    panel: "grid",
    families: [...CARTESIAN, "matrix"],
    excludeChartTypes: ["calendarHeatmap"],
  },
  {
    id: "gridBottom",
    path: "grid.bottom",
    type: "text",
    labelKey: "chartOptions.fields.gridBottom",
    hintKey: "chartOptions.hints.gridPercent",
    panel: "grid",
    families: [...CARTESIAN, "matrix"],
    excludeChartTypes: ["calendarHeatmap"],
  },
  {
    id: "gridContainLabel",
    path: "grid.containLabel",
    type: "boolean",
    labelKey: "chartOptions.fields.gridContainLabel",
    panel: "grid",
    families: [...CARTESIAN, "matrix"],
    excludeChartTypes: ["calendarHeatmap"],
  },
  {
    id: "xAxisName",
    path: "xAxis.name",
    type: "text",
    labelKey: "chartOptions.fields.xAxisName",
    panel: "axes",
    families: CARTESIAN,
  },
  {
    id: "xAxisLabelRotate",
    path: "xAxis.axisLabel.rotate",
    type: "number",
    labelKey: "chartOptions.fields.xAxisLabelRotate",
    panel: "axes",
    min: -90,
    max: 90,
    step: 15,
    families: CARTESIAN,
  },
  {
    id: "xAxisLabelShow",
    path: "xAxis.axisLabel.show",
    type: "boolean",
    labelKey: "chartOptions.fields.xAxisLabelShow",
    panel: "axes",
    families: CARTESIAN,
  },
  {
    id: "xAxisSplitLine",
    path: "xAxis.splitLine.show",
    type: "boolean",
    labelKey: "chartOptions.fields.xAxisSplitLine",
    panel: "axes",
    families: CARTESIAN,
  },
  {
    id: "yAxisName",
    path: "yAxis.name",
    type: "text",
    labelKey: "chartOptions.fields.yAxisName",
    panel: "axes",
    families: CARTESIAN,
  },
  {
    id: "yAxisMin",
    path: "yAxis.min",
    type: "number",
    labelKey: "chartOptions.fields.yAxisMin",
    panel: "axes",
    families: CARTESIAN,
  },
  {
    id: "yAxisMax",
    path: "yAxis.max",
    type: "number",
    labelKey: "chartOptions.fields.yAxisMax",
    panel: "axes",
    families: CARTESIAN,
  },
  {
    id: "yAxisSplitLine",
    path: "yAxis.splitLine.show",
    type: "boolean",
    labelKey: "chartOptions.fields.yAxisSplitLine",
    panel: "axes",
    families: CARTESIAN,
  },
  {
    id: "seriesColor",
    path: "series.0.itemStyle.color",
    type: "color",
    labelKey: "chartOptions.fields.seriesColor",
    panel: "series",
    families: [...CARTESIAN, ...CIRCULAR, "matrix", "flow"],
    excludeChartTypes: ["gauge", "radar", "parallel"],
  },
  {
    id: "barMaxWidth",
    path: "series.0.barMaxWidth",
    type: "number",
    labelKey: "chartOptions.fields.barMaxWidth",
    panel: "series",
    min: 1,
    max: 200,
    families: ["cartesian"],
    chartTypes: ["bar", "stackedBar", "mixed", "waterfall", "pictorialBar"],
  },
  {
    id: "barGap",
    path: "series.0.barGap",
    type: "text",
    labelKey: "chartOptions.fields.barGap",
    hintKey: "chartOptions.hints.barGap",
    panel: "series",
    families: ["cartesian"],
    chartTypes: ["bar", "stackedBar", "mixed", "waterfall"],
  },
  {
    id: "barCategoryGap",
    path: "series.0.barCategoryGap",
    type: "text",
    labelKey: "chartOptions.fields.barCategoryGap",
    hintKey: "chartOptions.hints.barGap",
    panel: "series",
    families: ["cartesian"],
    chartTypes: ["bar", "stackedBar", "mixed", "waterfall", "pictorialBar"],
  },
  {
    id: "lineSmooth",
    path: "series.0.smooth",
    type: "boolean",
    labelKey: "chartOptions.fields.lineSmooth",
    panel: "series",
    families: ["cartesian"],
    chartTypes: ["line", "area", "stackedLine", "stackedArea", "mixed"],
  },
  {
    id: "lineShowSymbol",
    path: "series.0.showSymbol",
    type: "boolean",
    labelKey: "chartOptions.fields.lineShowSymbol",
    panel: "series",
    families: ["cartesian"],
    chartTypes: ["line", "area", "stackedLine", "stackedArea", "mixed"],
  },
  {
    id: "lineWidth",
    path: "series.0.lineStyle.width",
    type: "number",
    labelKey: "chartOptions.fields.lineWidth",
    panel: "series",
    min: 1,
    max: 10,
    families: ["cartesian"],
    chartTypes: ["line", "area", "stackedLine", "stackedArea", "mixed"],
  },
  {
    id: "areaOpacity",
    path: "series.0.areaStyle.opacity",
    type: "number",
    labelKey: "chartOptions.fields.areaOpacity",
    panel: "series",
    min: 0,
    max: 1,
    step: 0.1,
    families: ["cartesian"],
    chartTypes: ["area", "stackedArea"],
  },
  {
    id: "stackName",
    path: "series.0.stack",
    type: "text",
    labelKey: "chartOptions.fields.stackName",
    panel: "series",
    families: ["cartesian"],
    chartTypes: ["stackedBar", "stackedLine", "stackedArea", "waterfall"],
  },
  {
    id: "scatterSymbolSize",
    path: "series.0.symbolSize",
    type: "number",
    labelKey: "chartOptions.fields.scatterSymbolSize",
    panel: "series",
    min: 2,
    max: 80,
    families: ["cartesian"],
    chartTypes: ["scatter", "effectScatter"],
  },
  {
    id: "pictorialSymbol",
    path: "series.0.symbol",
    type: "select",
    labelKey: "chartOptions.fields.pictorialSymbol",
    panel: "series",
    chartTypes: ["pictorialBar"],
    options: [
      { value: "roundRect", labelKey: "chartOptions.symbols.roundRect" },
      { value: "rect", labelKey: "chartOptions.symbols.rect" },
      { value: "triangle", labelKey: "chartOptions.symbols.triangle" },
      { value: "diamond", labelKey: "chartOptions.symbols.diamond" },
      { value: "pin", labelKey: "chartOptions.symbols.pin" },
    ],
  },
  {
    id: "pieRadius",
    path: "series.0.radius",
    type: "text",
    labelKey: "chartOptions.fields.pieRadius",
    hintKey: "chartOptions.hints.pieRadius",
    panel: "series",
    families: CIRCULAR,
    chartTypes: ["pie", "doughnut", "rosePie"],
  },
  {
    id: "roseType",
    path: "series.0.roseType",
    type: "select",
    labelKey: "chartOptions.fields.roseType",
    panel: "series",
    chartTypes: ["rosePie"],
    options: [
      { value: "radius", labelKey: "chartOptions.roseType.radius" },
      { value: "area", labelKey: "chartOptions.roseType.area" },
    ],
  },
  {
    id: "pieLabelShow",
    path: "series.0.label.show",
    type: "boolean",
    labelKey: "chartOptions.fields.pieLabelShow",
    panel: "series",
    families: CIRCULAR,
  },
  {
    id: "pieLabelPosition",
    path: "series.0.label.position",
    type: "select",
    labelKey: "chartOptions.fields.pieLabelPosition",
    panel: "series",
    families: CIRCULAR,
    chartTypes: ["pie", "doughnut", "rosePie"],
    options: [
      { value: "outside", labelKey: "chartOptions.labelPosition.outside" },
      { value: "inside", labelKey: "chartOptions.labelPosition.inside" },
      { value: "center", labelKey: "chartOptions.labelPosition.center" },
    ],
  },
  {
    id: "funnelSort",
    path: "series.0.sort",
    type: "select",
    labelKey: "chartOptions.fields.funnelSort",
    panel: "series",
    chartTypes: ["funnel"],
    options: [
      { value: "descending", labelKey: "chartOptions.sort.descending" },
      { value: "ascending", labelKey: "chartOptions.sort.ascending" },
      { value: "none", labelKey: "chartOptions.sort.none" },
    ],
  },
  {
    id: "funnelGap",
    path: "series.0.gap",
    type: "number",
    labelKey: "chartOptions.fields.funnelGap",
    panel: "series",
    min: 0,
    max: 100,
    chartTypes: ["funnel"],
  },
  {
    id: "visualMapMin",
    path: "visualMap.min",
    type: "number",
    labelKey: "chartOptions.fields.visualMapMin",
    panel: "visualMap",
    families: ["matrix"],
  },
  {
    id: "visualMapMax",
    path: "visualMap.max",
    type: "number",
    labelKey: "chartOptions.fields.visualMapMax",
    panel: "visualMap",
    families: ["matrix"],
  },
  {
    id: "visualMapOrient",
    path: "visualMap.orient",
    type: "select",
    labelKey: "chartOptions.fields.visualMapOrient",
    panel: "visualMap",
    families: ["matrix"],
    options: [
      { value: "horizontal", labelKey: "chartOptions.orient.horizontal" },
      { value: "vertical", labelKey: "chartOptions.orient.vertical" },
    ],
  },
  {
    id: "visualMapCalculable",
    path: "visualMap.calculable",
    type: "boolean",
    labelKey: "chartOptions.fields.visualMapCalculable",
    panel: "visualMap",
    families: ["matrix"],
  },
  {
    id: "heatmapLabelShow",
    path: "series.0.label.show",
    type: "boolean",
    labelKey: "chartOptions.fields.heatmapLabelShow",
    panel: "visualMap",
    chartTypes: ["heatmap"],
  },
  {
    id: "calendarRange",
    path: "calendar.range",
    type: "text",
    labelKey: "chartOptions.fields.calendarRange",
    hintKey: "chartOptions.hints.calendarRange",
    panel: "calendar",
    chartTypes: ["calendarHeatmap"],
  },
  {
    id: "calendarCellSize",
    path: "calendar.cellSize",
    type: "text",
    labelKey: "chartOptions.fields.calendarCellSize",
    hintKey: "chartOptions.hints.calendarCellSize",
    panel: "calendar",
    chartTypes: ["calendarHeatmap"],
  },
  {
    id: "calendarTop",
    path: "calendar.top",
    type: "number",
    labelKey: "chartOptions.fields.calendarTop",
    panel: "calendar",
    min: 0,
    max: 300,
    chartTypes: ["calendarHeatmap"],
  },
  {
    id: "radarShape",
    path: "radar.shape",
    type: "select",
    labelKey: "chartOptions.fields.radarShape",
    panel: "radar",
    chartTypes: ["radar"],
    options: [
      { value: "polygon", labelKey: "chartOptions.radarShape.polygon" },
      { value: "circle", labelKey: "chartOptions.radarShape.circle" },
    ],
  },
  {
    id: "radarSplitNumber",
    path: "radar.splitNumber",
    type: "number",
    labelKey: "chartOptions.fields.radarSplitNumber",
    panel: "radar",
    min: 1,
    max: 10,
    chartTypes: ["radar"],
  },
  {
    id: "radarRadius",
    path: "radar.radius",
    type: "text",
    labelKey: "chartOptions.fields.radarRadius",
    hintKey: "chartOptions.hints.pieRadius",
    panel: "radar",
    chartTypes: ["radar"],
  },
  {
    id: "radarAreaOpacity",
    path: "series.0.areaStyle.opacity",
    type: "number",
    labelKey: "chartOptions.fields.radarAreaOpacity",
    panel: "radar",
    min: 0,
    max: 1,
    step: 0.1,
    chartTypes: ["radar"],
  },
  {
    id: "gaugeMin",
    path: "series.0.min",
    type: "number",
    labelKey: "chartOptions.fields.gaugeMin",
    panel: "gauge",
    chartTypes: ["gauge"],
  },
  {
    id: "gaugeMax",
    path: "series.0.max",
    type: "number",
    labelKey: "chartOptions.fields.gaugeMax",
    panel: "gauge",
    chartTypes: ["gauge"],
  },
  {
    id: "gaugeSplitNumber",
    path: "series.0.splitNumber",
    type: "number",
    labelKey: "chartOptions.fields.gaugeSplitNumber",
    panel: "gauge",
    min: 1,
    max: 20,
    chartTypes: ["gauge"],
  },
  {
    id: "gaugeDetailFontSize",
    path: "series.0.detail.fontSize",
    type: "number",
    labelKey: "chartOptions.fields.gaugeDetailFontSize",
    panel: "gauge",
    min: 8,
    max: 48,
    chartTypes: ["gauge"],
  },
  {
    id: "gaugeAxisLineWidth",
    path: "series.0.axisLine.lineStyle.width",
    type: "number",
    labelKey: "chartOptions.fields.gaugeAxisLineWidth",
    panel: "gauge",
    min: 1,
    max: 40,
    chartTypes: ["gauge"],
  },
  {
    id: "sunburstRadius",
    path: "series.0.radius",
    type: "text",
    labelKey: "chartOptions.fields.sunburstRadius",
    hintKey: "chartOptions.hints.sunburstRadius",
    panel: "hierarchical",
    chartTypes: ["sunburst"],
  },
  {
    id: "treemapLeafDepth",
    path: "series.0.leafDepth",
    type: "number",
    labelKey: "chartOptions.fields.treemapLeafDepth",
    panel: "hierarchical",
    min: 1,
    max: 5,
    chartTypes: ["treemap"],
  },
  {
    id: "treemapRoam",
    path: "series.0.roam",
    type: "boolean",
    labelKey: "chartOptions.fields.treemapRoam",
    panel: "hierarchical",
    chartTypes: ["treemap"],
  },
  {
    id: "treeOrient",
    path: "series.0.orient",
    type: "select",
    labelKey: "chartOptions.fields.treeOrient",
    panel: "hierarchical",
    chartTypes: ["tree"],
    options: [
      { value: "LR", labelKey: "chartOptions.treeOrient.LR" },
      { value: "RL", labelKey: "chartOptions.treeOrient.RL" },
      { value: "TB", labelKey: "chartOptions.treeOrient.TB" },
      { value: "BT", labelKey: "chartOptions.treeOrient.BT" },
    ],
  },
  {
    id: "treeSymbolSize",
    path: "series.0.symbolSize",
    type: "number",
    labelKey: "chartOptions.fields.treeSymbolSize",
    panel: "hierarchical",
    min: 4,
    max: 32,
    chartTypes: ["tree"],
  },
  {
    id: "treeExpandCollapse",
    path: "series.0.expandAndCollapse",
    type: "boolean",
    labelKey: "chartOptions.fields.treeExpandCollapse",
    panel: "hierarchical",
    chartTypes: ["tree"],
  },
  {
    id: "sankeyNodeAlign",
    path: "series.0.nodeAlign",
    type: "select",
    labelKey: "chartOptions.fields.sankeyNodeAlign",
    panel: "flow",
    chartTypes: ["sankey"],
    options: [
      { value: "justify", labelKey: "chartOptions.sankeyAlign.justify" },
      { value: "left", labelKey: "chartOptions.pos.left" },
      { value: "right", labelKey: "chartOptions.pos.right" },
    ],
  },
  {
    id: "sankeyLayoutIterations",
    path: "series.0.layoutIterations",
    type: "number",
    labelKey: "chartOptions.fields.sankeyLayoutIterations",
    panel: "flow",
    min: 0,
    max: 128,
    chartTypes: ["sankey"],
  },
  {
    id: "sankeyFocus",
    path: "series.0.emphasis.focus",
    type: "select",
    labelKey: "chartOptions.fields.sankeyFocus",
    panel: "flow",
    chartTypes: ["sankey"],
    options: [
      { value: "adjacency", labelKey: "chartOptions.sankeyFocus.adjacency" },
      { value: "none", labelKey: "chartOptions.sankeyFocus.none" },
    ],
  },
  {
    id: "parallelLineWidth",
    path: "series.0.lineStyle.width",
    type: "number",
    labelKey: "chartOptions.fields.parallelLineWidth",
    panel: "flow",
    min: 1,
    max: 8,
    chartTypes: ["parallel"],
  },
];

export function getOptionFieldsForChartType(chartTypeId: string): ChartOptionFieldDef[] {
  const type = normalizeChartTypeId(chartTypeId);
  const def = getChartTypeDef(type);
  if (!def) return [];

  return CHART_OPTION_FIELDS.filter((field) => {
    if (field.excludeChartTypes?.includes(def.id)) return false;
    if (field.chartTypes && !field.chartTypes.includes(def.id)) return false;
    if (field.families && !field.families.includes(def.family)) return false;
    return true;
  });
}

export function getOptionPanelsForChartType(chartTypeId: string): ChartOptionPanelId[] {
  const panelSet = new Set(getOptionFieldsForChartType(chartTypeId).map((f) => f.panel));
  return CHART_OPTION_PANEL_ORDER.filter((p) => panelSet.has(p));
}

export function groupOptionFieldsByPanel(
  chartTypeId: string
): Array<{ panel: ChartOptionPanelId; fields: ChartOptionFieldDef[] }> {
  const fields = getOptionFieldsForChartType(chartTypeId);
  return getOptionPanelsForChartType(chartTypeId).map((panel) => ({
    panel,
    fields: fields.filter((f) => f.panel === panel),
  }));
}
